/**
 * <MessageAttachment> — ce qu'un message porte en plus de son texte (molecule).
 *
 * Trois formes, celles que la messagerie de l'élève sert aujourd'hui : une image, un document,
 * un message vocal. Elle se pose DANS une bulle (`<MessageBubble>`), qui lui donne son encre.
 *
 * Ce n'est pas `<ImageAttachment>` / `<PDFAttachment>` : ceux-là sont les aperçus d'un fichier
 * qu'on vient de choisir, avec leur intitulé et leur corbeille. Un message envoyé ne se retire
 * pas, et sa bulle est déjà sa carte.
 *
 * Contrôlé : la lecture d'un vocal (`playing`, `progress`) arrive en props, le lecteur audio
 * lui-même est un effet de l'écran. Toucher l'image ou le document remonte `onOpen` ; la
 * visionneuse (`<MediaDisplay>`) appartient à l'écran.
 *
 * Un vocal chargé se règle comme dans l'ancien fil (`voiceControls`) : sa barre devient un curseur
 * qu'on touche ou qu'on glisse, l'heure dit où en est la lecture sur sa durée (« 0:12 / 0:42 »), et
 * la vitesse passe de 1x à 1,5x, 2x, puis revient. Sans `voiceControls`, la lecture seule d'avant.
 *
 * Une photo ou un document dont le tuteur a tiré une activité porte sa pastille (`activityStatus`) :
 * l'ancien fil la posait en bas à droite de la vignette (`DoneChip`).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { ProgressBar } from "../../atoms/ProgressBar"
import { SeekBar } from "../../atoms/SeekBar"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { ActivityStatusBadge } from "../ActivityStatusBadge"
import { voiceRateLabel, voiceTimeLabel } from "../../../ports/primary/ui/formatters/voiceFormatter"
import { type UIMessageAttachment } from "../../../ports/primary/ui/types/studentConversation"

/** Ce qui règle un vocal chargé : sa vitesse, où en est la lecture, et les gestes qui les changent. */
export type MessageVoiceControls = {
    /** 1, 1,5 ou 2. */
    rate: number
    onCycleRate: () => void
    /** La position choisie, de 0 à 1. */
    onSeek: (ratio: number) => void
    /** Où en est la lecture — « 0:12 ». Absent : la durée seule. */
    elapsedLabel?: string
}

// La forme de la pièce vit dans `ports/primary/ui/types` (remontée au câblage de la messagerie).
export type { UIMessageAttachment }

export type MessageAttachmentProps = {
    attachment: UIMessageAttachment
    /** L'encre de la bulle qui la porte — `text-primary` par défaut. */
    ink?: SemanticColorKey
    /** L'aplat de la bulle : le bouton de lecture d'un vocal s'y découpe. `surface-raised` par défaut. */
    fill?: SemanticColorKey
    /** La piste de l'avancée d'un vocal, lisible sur l'aplat de la bulle — `border` par défaut. */
    track?: SemanticColorKey
    /** Image ou document touché : l'écran ouvre sa visionneuse. Absent, rien ne presse. */
    onOpen?: () => void
    /** Vocal : en lecture. */
    playing?: boolean
    /** Vocal : l'avancée de la lecture, de 0 à 1. */
    progress?: number
    /** Vocal : lire ou mettre en pause. Absent, le bouton ne répond pas. */
    onTogglePlay?: () => void
    /** Vocal chargé : le curseur et la vitesse. Absent : la barre en lecture seule. */
    voiceControls?: MessageVoiceControls
    testID?: string
}

/** Le côté de la vignette d'une image dans le fil. */
const MESSAGE_IMAGE_SIZE = 176
/** La largeur d'un message vocal : assez pour lire l'avancée, pas plus large qu'une bulle de téléphone. */
const VOICE_WIDTH = 216
/** L'ancien vocal faisait 260 px de large, avec son heure longue et sa vitesse. */
const VOICE_CONTROLS_WIDTH = 296
const PLAY_SIZE = 44

const TEXTS = {
    document: "Document PDF",
    openImage: "Ouvrir la photo",
    play: "Écouter le message vocal",
    pause: "Mettre en pause",
    seek: "Position dans le message vocal",
    rate: (label: string) => `Vitesse de lecture : ${label}`,
}

export function MessageAttachment(props: MessageAttachmentProps) {
    const { attachment, ink = "text-primary", fill = "surface-raised", onOpen, testID } = props

    if (attachment.kind === "image") {
        const thumbnail = (
            <MediaThumbnail
                uri={attachment.uri}
                size={MESSAGE_IMAGE_SIZE}
                onPress={onOpen}
                accessibilityLabel={TEXTS.openImage}
                testID={testID}
            />
        )
        if (attachment.activityStatus == null) return thumbnail
        return (
            <View style={styles.framed}>
                {thumbnail}
                {/* Le toucher passe à la vignette : la pastille ne fait que dire. */}
                <View style={styles.imageStatus} pointerEvents="none">
                    <ActivityStatusBadge status={attachment.activityStatus} />
                </View>
            </View>
        )
    }

    if (attachment.kind === "document") {
        const name = attachment.name ?? TEXTS.document
        return (
            <Pressable
                onPress={onOpen}
                disabled={onOpen == null}
                accessibilityRole="button"
                accessibilityLabel={name}
                style={styles.document}
                testID={testID}
            >
                <Icon name="file-pdf" size={20} color={ink} />
                <Text size="text-lg" weight="semibold" color={ink} numberOfLines={1} style={styles.shrink}>
                    {name}
                </Text>
                {attachment.activityStatus != null && <ActivityStatusBadge status={attachment.activityStatus} />}
            </Pressable>
        )
    }

    const { track = "border", playing = false, progress = 0, onTogglePlay, voiceControls } = props
    const timeLabel = voiceTimeLabel(attachment.durationLabel, voiceControls?.elapsedLabel)

    return (
        <Row gap="2" align="center" style={styles.voice(voiceControls != null)} testID={testID}>
            <IconButton
                iconName={playing ? "pause" : "play"}
                size={PLAY_SIZE}
                color={ink}
                iconColor={fill}
                onPress={onTogglePlay}
                accessibilityLabel={playing ? TEXTS.pause : TEXTS.play}
            />
            <View style={styles.shrink}>
                {voiceControls != null ? (
                    <SeekBar
                        value={progress}
                        onSeek={voiceControls.onSeek}
                        color={ink}
                        trackColor={track}
                        accessibilityLabel={TEXTS.seek}
                    />
                ) : (
                    <ProgressBar value={progress * 100} color={ink} trackColor={track} height={4} />
                )}
            </View>
            <Text size="text-sm" weight="semibold" color={ink}>
                {timeLabel}
            </Text>
            {voiceControls != null && (
                <Button
                    label={voiceRateLabel(voiceControls.rate)}
                    variant="ghost"
                    size="sm"
                    textColor={ink}
                    onPress={voiceControls.onCycleRate}
                    accessibilityLabel={TEXTS.rate(voiceRateLabel(voiceControls.rate))}
                />
            )}
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Une ligne qui presse : 44 px, la cible d'un doigt. */
    document: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["2"],
        minHeight: 44,
    },
    /** Réglable, le vocal gagne la place de l'heure longue et de la vitesse. */
    voice: (controls: boolean) => ({
        width: controls ? VOICE_CONTROLS_WIDTH : VOICE_WIDTH,
        maxWidth: "100%" as const,
    }),
    shrink: { flex: 1, flexShrink: 1 },
    /** La vignette garde son emprise : la pastille se pose dans son coin. */
    framed: { alignSelf: "flex-start" as const },
    imageStatus: {
        position: "absolute" as const,
        right: theme.spacing["2"],
        bottom: theme.spacing["2"],
    },
}))
